import { Mail, MessageSquare, MessagesSquare } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import { Badge } from "@/app/admin/_common/ui/badge";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import CuoButton from "@/app/admin/_common/CuoButton";
import { formatUtcDateTime } from "@/app/_utils/dayjs";
import { cn } from "@/app/_utils/cn";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";
import type { Message } from "@/app/_services/CommunicationsService";

import {
  SOURCE_LABELS,
  statusBadgeClasses,
  statusDotClasses,
  statusLabel,
} from "./messageStatus";

function StatusPill({ channel, status }: { channel: string; status: string }) {
  return (
    <Badge
      variant="outline"
      className={cn("gap-1.5 rounded-[7px] font-normal", statusBadgeClasses(status))}
    >
      <span className={cn("size-1.5 flex-none rounded-full", statusDotClasses(status))} />
      {statusLabel(channel, status)}
    </Badge>
  );
}

function ChannelIcon({ channel }: { channel: string }) {
  const Icon = channel === "sms" ? MessageSquare : Mail;
  return <Icon className="size-3.5 flex-none text-muted-foreground" />;
}

/** SMS has no subject, so its body carries the line. */
function subjectLine(message: Message) {
  return message.subject || message.preview || "—";
}

function sourceLine(message: Message) {
  const source = SOURCE_LABELS[message.source] ?? message.source;
  return message.source_label ? `${source} · ${message.source_label}` : source;
}

export default function CommunicationsTable({
  messages,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onSelect,
  hasFilters,
}: {
  messages: Message[];
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onSelect: (message: Message) => void;
  hasFilters: boolean;
}) {
  const isMobile = useIsMobile();

  const emptyText = hasFilters
    ? "No messages match these filters"
    : "Nothing sent yet";

  const loadMoreRow = hasMore && (
    <div className="flex justify-center border-t border-border py-3">
      <CuoButton
        variant="outline"
        label={isLoadingMore ? "Loading…" : "Load more"}
        disabled={isLoadingMore}
        onClick={onLoadMore}
      />
    </div>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col bg-card">
        {messages.length === 0 ? (
          <div className="flex min-h-[400px] flex-col">
            <CuoEmpty icon={MessagesSquare} text={emptyText} />
          </div>
        ) : (
          <div className="flex flex-col">
            {messages.map((message) => (
              <div
                key={message.id}
                className="flex cursor-pointer flex-col gap-1 border-b border-border px-4 py-2.5 last:border-0"
                onClick={() => onSelect(message)}
              >
                <div className="flex items-center gap-1.5">
                  <ChannelIcon channel={message.channel} />
                  <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-foreground">
                    {message.recipient}
                  </span>
                  <StatusPill channel={message.channel} status={message.status} />
                </div>
                <div className="truncate text-[12.5px] text-muted-foreground">
                  {subjectLine(message)}
                </div>
                <div className="text-[11.5px] text-muted-foreground">
                  {sourceLine(message)} · {formatUtcDateTime(message.created_at)}
                </div>
              </div>
            ))}
            {loadMoreRow}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[1000px] table-fixed", messages.length === 0 && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-[5%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Type
            </TableHead>
            <TableHead className="h-8 w-[20%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Recipient
            </TableHead>
            <TableHead className="h-8 w-[27%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Message
            </TableHead>
            <TableHead className="h-8 w-[16%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Source
            </TableHead>
            <TableHead className="h-8 w-[12%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Status
            </TableHead>
            <TableHead className="h-8 w-[20%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Sent
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {messages.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={6} className="h-full p-0">
                <CuoEmpty icon={MessagesSquare} text={emptyText} />
              </TableCell>
            </TableRow>
          ) : (
            messages.map((message) => (
              <TableRow
                key={message.id}
                // Overrides TableCell's default p-4 once, rather than on all six cells.
                className="group cursor-pointer hover:bg-muted/50 [&>td]:px-4 [&>td]:py-2"
                onClick={() => onSelect(message)}
              >
                <TableCell>
                  <ChannelIcon channel={message.channel} />
                </TableCell>
                <TableCell className="truncate text-[12.5px] text-foreground">
                  <span className="group-hover:underline">{message.recipient}</span>
                </TableCell>
                <TableCell className="truncate text-[12.5px] text-foreground">
                  {subjectLine(message)}
                </TableCell>
                <TableCell className="truncate text-[12.5px] text-muted-foreground">
                  {sourceLine(message)}
                </TableCell>
                <TableCell>
                  <StatusPill channel={message.channel} status={message.status} />
                </TableCell>
                <TableCell className="whitespace-nowrap text-[12.5px] text-muted-foreground">
                  {formatUtcDateTime(message.created_at)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {loadMoreRow}
    </div>
  );
}
